import { ReactNode, Suspense } from 'react';

import { Outlet, useParams } from 'react-router';

import { NewRoutesWrapperArrayType, NewRoutesWrapperType } from './utils';
import { RequireAuthenticatedBuyer } from '../auth/Auth';
import { Account } from '../Buyer/Account/Account';
import { BuyerMenu } from '../Buyer/Account/components/BuyerMenu/BuyerMenu';
import { SellerDirectory } from '../Buyer/Account/SellerDirectory/SellerDirectory';
import { BuyerCrossSellForm } from '../Buyer/BuyerCrossSellForm/BuyerCrossSellForm';
import { OrderCheckoutPage } from '../Buyer/Orders/OrderCheckoutPage/OrderCheckoutPage';
import OrderLandingPage from '../Buyer/Orders/OrderLandingPage/OrderLandingPage';
import { GenericPayLinkLanding as GenericPayLinkLandingPage } from '../Buyer/PayLinkLanding/GenericPayLinkLanding/GenericPayLinkLanding';
import { PayLinkLanding as PayLinkLandingPage } from '../Buyer/PayLinkLanding/PayLinkLanding';
import { PayNowConfirmation } from '../Buyer/PayNowConfirmation/PayNowConfirmation';
import { SpendingLimitRequestApproved } from '../Buyer/Signup/components/SpendingLimitRequestDecision/SpendingLimitRequestApproved';
import { SignupLayout } from '../Buyer/Signup/SignupLayout';
import { ChooseProduct } from '../Buyer/Signup/steps/NewChooseProduct/ChooseProduct';
import { SpendingLimitApproved } from '../Buyer/Signup/steps/SpendingLimitApproved/SpendingLimitApproved';
import { SpendingLimitRequestLanding } from '../Buyer/SpendingLimitRequest/SpendingLimitRequestLanding';
import { LoadingScreen } from '../components/LoadingScreen/LoadingScreen';
import { PayLinkNotFound } from '../Pages/FailurePages/PayLinkNotFound';
import { XeroPayLinkError } from '../Pages/FailurePages/XeroPayLinkError/XeroPayLinkError';

const BuyerPageTemplate = ({ children }: { children: ReactNode }) => {
  return (
    <div className="min-h-screen w-full bg-app-gradient text-center">
      {children}
    </div>
  );
};

const GenericPayLink = () => {
  const { sellerHandle, payLinkPackageId } = useParams();

  return (
    <main className="h-full min-h-screen w-full flex-auto text-white">
      {sellerHandle && (
        <GenericPayLinkLandingPage
          sellerHandle={sellerHandle}
          payLinkPackageId={payLinkPackageId}
        />
      )}
    </main>
  );
};

const PayLinkLandingClone = () => {
  const { payLinkId } = useParams();

  return (
    <main className="h-full min-h-screen w-full flex-auto text-white">
      {payLinkId ? (
        <PayLinkLandingPage payLinkId={payLinkId} />
      ) : (
        <PayLinkNotFound />
      )}
    </main>
  );
};

const SPENDING_LIMIT = [
  {
    path: 'spending-account/:sellerHandle',
    element: <SpendingLimitRequestLanding />,
  },
  {
    path: 'spending-account/approved',
    element: <SpendingLimitRequestApproved />,
  },
] as const satisfies NewRoutesWrapperType['paths'];

const PAY_LINK_LANDINGS = [
  {
    path: 'me/:sellerHandle',
    element: <GenericPayLink />,
  },
  {
    path: 'to/:seller/:payLinkId',
    element: <PayLinkLandingClone />,
  },
  {
    path: 'me/:sellerHandle/package/:payLinkPackageId',
    element: <GenericPayLink />,
  },
] as const satisfies NewRoutesWrapperType['paths'];

const XERO_ERRORS = [
  {
    path: 'xero-error',
    element: <XeroPayLinkError />,
  },
] as const satisfies NewRoutesWrapperType['paths'];

const PAY_NOW = [
  {
    path: 'pay-now/confirmation',
    element: <PayNowConfirmation />,
  },
  {
    path: 'pay-now/confirmation/overlay',
    element: <PayNowConfirmation />,
  },
] as const satisfies NewRoutesWrapperType['paths'];

const ORDER = [
  {
    path: 'order/:orderId/landing',
    element: <OrderLandingPage />,
  },
  {
    path: 'order/:orderId',
    element: <OrderCheckoutPage />,
  },
] as const satisfies NewRoutesWrapperType['paths'];

const LOADING = [
  {
    path: 'loading',
    element: <LoadingScreen />,
  },
] as const satisfies NewRoutesWrapperType['paths'];

const APPROVED = [
  {
    path: 'approved',
    element: <SpendingLimitApproved />,
  },
] as const satisfies NewRoutesWrapperType['paths'];

const CHOOSE_PRODUCT = [
  {
    path: 'choose-product',
    element: (
      <SignupLayout wideWidth={true}>
        <ChooseProduct />
      </SignupLayout>
    ),
  },
] as const satisfies NewRoutesWrapperType['paths'];

const DASHBOARD_PAGES = [
  { path: 'account', element: <Account /> },
  {
    path: 'account/directory',
    element: <SellerDirectory />,
  },
] as const satisfies NewRoutesWrapperType['paths'];

const APPLY_FLEXI_LOAN = [
  {
    path: 'apply-flexi-loan',
    element: (
      <RequireAuthenticatedBuyer>
        <Suspense fallback={<LoadingScreen />}>
          <BuyerCrossSellForm />
        </Suspense>
      </RequireAuthenticatedBuyer>
    ),
  },
];

const STANDARD_BUYER_PAGES = {
  wrapper: (
    <BuyerPageTemplate>
      <Outlet />
    </BuyerPageTemplate>
  ),
  paths: [
    ...SPENDING_LIMIT,
    ...PAY_LINK_LANDINGS,
    ...XERO_ERRORS,
    ...PAY_NOW,
    ...ORDER,
    ...LOADING,
    ...APPROVED,
    ...CHOOSE_PRODUCT,
    ...APPLY_FLEXI_LOAN,
  ],
} as const satisfies NewRoutesWrapperType;

const DASHBOARD_BUYER_PAGES = {
  wrapper: (
    <RequireAuthenticatedBuyer>
      <div className="px-l">
        <BuyerMenu />
        <Outlet />
      </div>
    </RequireAuthenticatedBuyer>
  ),
  paths: [...DASHBOARD_PAGES],
} as const satisfies NewRoutesWrapperType;

export const BUYER_ROUTES = [
  STANDARD_BUYER_PAGES,
  DASHBOARD_BUYER_PAGES,
] as const satisfies NewRoutesWrapperArrayType;

export const buyerPaths = [
  ...SPENDING_LIMIT,
  ...PAY_LINK_LANDINGS,
  ...XERO_ERRORS,
  ...PAY_NOW,
  ...ORDER,
  ...LOADING,
  ...APPROVED,
  ...CHOOSE_PRODUCT,
  ...DASHBOARD_PAGES,
  ...APPLY_FLEXI_LOAN,
];
